import { useEffect, useRef } from 'react';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function ParticleField() {
  const canvasRef = useRef(null);

  useEffect(() => {
    let animId, renderer, scene, camera, particles, linesMesh;
    let mouse = { x: 0, y: 0 };
    const isMobile = window.innerWidth < 768;
    const COUNT = isMobile ? 800 : 2000;

    const onMouse = (e) => {
      mouse.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMouse);

    loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js')
      .then(() => {
        const THREE = window.THREE;
        const canvas = canvasRef.current;
        if (!canvas) return;

        renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(window.innerWidth, window.innerHeight);

        scene = new THREE.Scene();
        scene.background = new THREE.Color(0x05080f);

        camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        camera.position.z = 80;

        // Particles
        const geo = new THREE.BufferGeometry();
        const pos = new Float32Array(COUNT * 3);
        const colors = new Float32Array(COUNT * 3);
        for (let i = 0; i < COUNT; i++) {
          pos[i * 3]     = (Math.random() - 0.5) * 240;
          pos[i * 3 + 1] = (Math.random() - 0.5) * 140;
          pos[i * 3 + 2] = (Math.random() - 0.5) * 120;
          if (Math.random() < 0.3) {
            colors[i * 3] = 1; colors[i * 3 + 1] = 0.42; colors[i * 3 + 2] = 0;
          } else {
            colors[i * 3] = 0.9; colors[i * 3 + 1] = 0.9; colors[i * 3 + 2] = 0.9;
          }
        }
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        const mat = new THREE.PointsMaterial({ size: 0.4, vertexColors: true, transparent: true, opacity: 0.6, sizeAttenuation: true });
        particles = new THREE.Points(geo, mat);
        scene.add(particles);

        // Connection lines (rebuilt each frame, capped at 150)
        const lineGeo = new THREE.BufferGeometry();
        const lineMat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.04 });
        linesMesh = new THREE.LineSegments(lineGeo, lineMat);
        scene.add(linesMesh);

        const onResize = () => {
          camera.aspect = window.innerWidth / window.innerHeight;
          camera.updateProjectionMatrix();
          renderer.setSize(window.innerWidth, window.innerHeight);
        };
        window.addEventListener('resize', onResize);

        const posArr = geo.attributes.position.array;
        const loop = () => {
          animId = requestAnimationFrame(loop);
          particles.rotation.y += 0.0003;
          particles.rotation.x += 0.0001;
          camera.position.x += (mouse.x * 1.5 - camera.position.x) * 0.02;
          camera.position.y += (-mouse.y * 1.5 - camera.position.y) * 0.02;
          camera.lookAt(scene.position);

          if (!isMobile) {
            const lineVerts = [];
            let count = 0;
            for (let i = 0; i < COUNT && count < 150; i++) {
              for (let j = i + 1; j < COUNT && count < 150; j++) {
                const dx = posArr[i*3] - posArr[j*3];
                const dy = posArr[i*3+1] - posArr[j*3+1];
                const dz = posArr[i*3+2] - posArr[j*3+2];
                if (dx*dx + dy*dy + dz*dz < 18*18) {
                  lineVerts.push(posArr[i*3], posArr[i*3+1], posArr[i*3+2]);
                  lineVerts.push(posArr[j*3], posArr[j*3+1], posArr[j*3+2]);
                  count++;
                }
              }
            }
            linesMesh.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lineVerts), 3));
          }

          renderer.render(scene, camera);
        };
        loop();

        return () => window.removeEventListener('resize', onResize);
      });

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMouse);
      renderer?.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', width: '100%', height: '100%' }}
    />
  );
}
